/**
 * Regras de status programado para notícias (admin + cliente).
 *
 * Hierarquia (1 = vence em caso de conflito no mesmo dia civil de agenda):
 * 1 — Destaque Naviex
 * 2 — Destaque Principal
 * 3 — Destaque Categoria
 * 4 — Ativada
 * 5 — Desativada
 *
 * Troca de “dia” de agenda: às 00:10:10 (horário local). Antes desse instante,
 * ainda conta o dia civil anterior para efeitos de qual período está vigente.
 */

/** Segundos desde meia-noite até o corte do dia de agenda (00:10:10). */
export const SEGUNDOS_CORTE_DIA_AGENDA_STATUS = 10 * 60 + 10;

const PRIORIDADE_TIPO: Record<string, number> = {
  naviex: 1,
  principal: 2,
  categoria: 3,
  ativada: 4,
  /** Hospedagens / eventos (admin) */
  ativado: 4,
  desativada: 5,
  desativado: 5,
};

export function ymdFromLocalDate(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * YYYY-MM-DD do “dia de agenda” vigente neste instante (corte 00:10:10 local).
 */
export function ymdReferenciaAgendaStatusLocal(now: Date = new Date()): string {
  const sec = now.getHours() * 3600 + now.getMinutes() * 60 + now.getSeconds();
  const ref = new Date(now);
  if (sec < SEGUNDOS_CORTE_DIA_AGENDA_STATUS) {
    ref.setDate(ref.getDate() - 1);
  }
  return ymdFromLocalDate(ref);
}

/** Menor número = maior prioridade. Tipos desconhecidos perdem para os definidos. */
export function prioridadeStatusProgramadoNoticia(tipo: string): number {
  return PRIORIDADE_TIPO[tipo] ?? 99;
}

function normalizaIntervalo(de: string, ate: string): { lo: string; hi: string } {
  return de <= ate ? { lo: de, hi: ate } : { lo: ate, hi: de };
}

/**
 * Entre todos os períodos que cobrem `diaYmd` (inclusive), devolve o `tipo` com maior prioridade.
 */
export function tipoStatusProgramadoDominanteNoDia(
  periodos: readonly { de: string; ate: string; tipo: string }[] | undefined,
  diaYmd: string
): string | null {
  let best: { tipo: string; pr: number } | null = null;
  for (const p of periodos ?? []) {
    const { lo, hi } = normalizaIntervalo(p.de, p.ate);
    if (diaYmd < lo || diaYmd > hi) continue;
    const pr = prioridadeStatusProgramadoNoticia(p.tipo);
    if (!best || pr < best.pr) best = { tipo: p.tipo, pr };
  }
  return best?.tipo ?? null;
}

/**
 * Valores de `statusPadrao` da experiência (fora de períodos no calendário).
 * Qualquer valor exceto `desativada` conta como ativa no catálogo.
 */
export type ExperienciaStatusPadraoValor =
  | 'ativada'
  | 'desativada'
  | 'naviex'
  | 'principal'
  | 'categoria';

/** Fora de períodos programados: experiência entra como ativa no cliente/guia? */
export function experienciaStatusPadraoContaComoAtiva(statusPadrao?: string): boolean {
  const p = statusPadrao ?? 'ativada';
  return p !== 'desativada' && p !== 'desativado';
}
